import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GridService } from '../shared/grid/grid.service';
import { GridModel } from '../shared/grid/grid.model';
import { GridExport } from '../models/gridExport.model';
import { SurveyTemplate, SurveyTemplateListResponse, SurveyTemplateUserDefault } from '../models/surveyTemplate.model';

@Injectable({
  providedIn: 'root'
})
export class SurveyTemplateService extends GridService<SurveyTemplate> {

  constructor(http: HttpClient) {
    super('SurveyTemplate', http);
  }

  override get(filter: string, orderBy: string, sort: string, page: number, pageSize: number, filterBy: string, headers?: HttpHeaders): Observable<GridModel<SurveyTemplateListResponse>> {
    return this.http.get<GridModel<SurveyTemplateListResponse>>(this.getApiUrl(`${this.controllerName}/GetList`), {
      params: this.createParamsWithFilterBy(filter, orderBy, sort, page, pageSize, filterBy),
      headers
    }).pipe(
      map(model => new GridModel<SurveyTemplateListResponse>(
        model.data.map(row => ({ ...row, isLocked: !row.canManage })),
        model.count
      ))
    );
  }

  override export(request: GridExport, headers?: HttpHeaders): Observable<HttpResponse<Blob>> {
    return this.http.post(
      this.getApiUrl(`${this.controllerName}/Export`),
      request,
      { responseType: 'blob', observe: 'response', headers }
    );
  }

  duplicate(id: string, name: string): Observable<SurveyTemplate> {
    return this.http.post<SurveyTemplate>(
      this.getApiUrl(`${this.controllerName}/Duplicate`),
      null,
      { params: { id, name } }
    );
  }

  getDefault(): Observable<SurveyTemplate | null> {
    return this.http.get<SurveyTemplate | null>(this.getApiUrl(`${this.controllerName}/GetDefault`));
  }

  setDefault(dto: SurveyTemplateUserDefault): Observable<SurveyTemplateUserDefault> {
    return this.http.post<SurveyTemplateUserDefault>(
      this.getApiUrl(`${this.controllerName}/SetDefault`),
      dto
    );
  }

  clearDefault(): Observable<boolean> {
    return this.http.post<boolean>(
      this.getApiUrl(`${this.controllerName}/ClearDefault`),
      null
    );
  }

  rename(template: SurveyTemplate, newName: string): Observable<Object> {
    return this.update({ ...template, name: newName });
  }

  setShared(template: SurveyTemplate, isShared: boolean, organizationLevelId?: string | null): Observable<Object> {
    // The API's OrganizationLevelId is a non-nullable Guid: un-sharing passes null, which keeps the template's own level.
    const resolvedOrgLevelId = organizationLevelId ?? template.organizationLevelId;
    return this.update({ ...template, isShared, organizationLevelId: resolvedOrgLevelId });
  }
}
